/**
 * Liste d'envies sans compte (Benchmark 4.0 — P1, CDC §7.1), 100 % côté client.
 *
 * Sauvegarde locale (clé « boteh:wishlist » dans localStorage) — aucune donnée
 * envoyée à un serveur, aucun compte requis. 89 % des sites forcent l'inscription
 * pour sauvegarder un produit et perdent les indécis (Baymard) : ici, un clic
 * suffit. La synchro entre appareils (nécessiterait une app) est hors périmètre
 * (voir docs/OPPORTUNITES-FUTURES.md).
 *
 * Trois custom elements, un seul module chargé dans le header (donc présent
 * partout ; il « upgrade » automatiquement les boutons/compteur/page) :
 *   <wishlist-button> — bouton toggle « Enregistrer » (cartes + fiche produit)
 *   <wishlist-count>  — badge compteur dans le header
 *   <wishlist-page>   — grille de la page dédiée
 *
 * Amélioration progressive : les boutons et le badge sont construits par le JS.
 * Sans JavaScript, rien ne s'affiche (aucun bouton parasite, cf. CLAUDE.md).
 *
 * Sécurité : le DOM des cartes est construit via createElement/textContent
 * (jamais innerHTML) — les données viennent de localStorage, modifiable par
 * n'importe quel script du navigateur, donc traitées comme non fiables.
 * Les icônes (statiques, notre propre code) sont clonées depuis des <template>.
 */
const STORAGE_KEY = 'boteh:wishlist';
const MAX_ITEMS = 50;
const CHANGE_EVENT = 'wishlist:change';

/* ------------------------------------------------------------------ */
/* Stockage partagé                                                    */
/* ------------------------------------------------------------------ */

/** Lit la liste, tolérante aux données corrompues (retourne toujours un tableau). */
function readList() {
  let items;
  try {
    items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch (error) {
    return [];
  }
  if (!Array.isArray(items)) return [];
  return items.filter((item) => item && typeof item.handle === 'string');
}

/** Écrit la liste (capée). Dégradation silencieuse si le stockage est indisponible. */
function writeList(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch (error) {
    /* navigation privée / quota dépassé — on n'interrompt pas l'utilisateur */
  }
}

function hasItem(handle) {
  return readList().some((item) => item.handle === handle);
}

/** Ajoute (plus récent en tête) ou retire selon l'état courant. Retourne l'état « enregistré ». */
function toggleItem(record) {
  const items = readList();
  const index = items.findIndex((item) => item.handle === record.handle);
  let saved;
  if (index >= 0) {
    items.splice(index, 1);
    saved = false;
  } else {
    items.unshift(record);
    saved = true;
  }
  writeList(items);
  notifyChange();
  return saved;
}

function removeItem(handle) {
  writeList(readList().filter((item) => item.handle !== handle));
  notifyChange();
}

/** Prévient tous les composants de l'onglet courant (les autres onglets via l'événement natif « storage »). */
function notifyChange() {
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/* ------------------------------------------------------------------ */
/* Utilitaires de rendu                                                */
/* ------------------------------------------------------------------ */

function createMoney(locale, currency) {
  try {
    return new Intl.NumberFormat(locale || 'fr', { style: 'currency', currency: currency || 'EUR' });
  } catch (error) {
    return null;
  }
}

function formatPrice(price, money) {
  if (typeof price !== 'number') return '';
  return money ? money.format(price / 100) : (price / 100).toFixed(2);
}

/** N'accepte que les chemins relatifs (les URL produit Liquid le sont toujours). */
function safeUrl(value) {
  return typeof value === 'string' && value.startsWith('/') ? value : '#';
}

/* ------------------------------------------------------------------ */
/* Mixin : abonnement aux changements (même onglet + inter-onglets)    */
/* ------------------------------------------------------------------ */

function subscribe(host) {
  host._onChange = () => host.render();
  host._onStorage = (event) => {
    if (event.key === STORAGE_KEY) host.render();
  };
  document.addEventListener(CHANGE_EVENT, host._onChange);
  window.addEventListener('storage', host._onStorage);
}

function unsubscribe(host) {
  if (host._onChange) document.removeEventListener(CHANGE_EVENT, host._onChange);
  if (host._onStorage) window.removeEventListener('storage', host._onStorage);
}

/* ------------------------------------------------------------------ */
/* <wishlist-button> — bouton toggle sur cartes et fiche produit       */
/* ------------------------------------------------------------------ */

class WishlistButton extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-wishlist-record]');
    if (!dataEl) return;
    try {
      this.record = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    if (!this.record || typeof this.record.handle !== 'string') return;

    this.context = this.getAttribute('data-context') || 'card';
    this.labelAdd = this.getAttribute('data-label-add') || '';
    this.labelRemove = this.getAttribute('data-label-remove') || '';

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'wishlist-button__toggle';

    const iconTpl = this.querySelector('[data-wishlist-icon]');
    if (iconTpl && iconTpl.content) {
      this.button.appendChild(iconTpl.content.cloneNode(true));
    }

    if (this.context === 'product') {
      this.text = document.createElement('span');
      this.text.className = 'wishlist-button__text';
      this.button.appendChild(this.text);
    }

    this.button.addEventListener('click', () => toggleItem(this.record));
    this.appendChild(this.button);

    subscribe(this);
    this.render();
  }

  disconnectedCallback() {
    unsubscribe(this);
  }

  render() {
    if (!this.button) return;
    const saved = hasItem(this.record.handle);
    const label = saved ? this.labelRemove : this.labelAdd;
    this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    this.button.setAttribute('aria-label', label);
    this.button.title = label;
    this.classList.toggle('is-saved', saved);
    if (this.text) this.text.textContent = label;
  }
}

/* ------------------------------------------------------------------ */
/* <wishlist-count> — badge compteur du header                         */
/* ------------------------------------------------------------------ */

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.setAttribute('aria-hidden', 'true');
    subscribe(this);
    this.render();
  }

  disconnectedCallback() {
    unsubscribe(this);
  }

  render() {
    const count = readList().length;
    this.textContent = count > 0 ? String(count) : '';
    this.toggleAttribute('hidden', count === 0);
  }
}

/* ------------------------------------------------------------------ */
/* <wishlist-page> — grille de la page dédiée                          */
/* ------------------------------------------------------------------ */

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.locale = this.getAttribute('data-locale') || 'fr';
    this.currency = this.getAttribute('data-currency') || 'EUR';
    this.labelRemove = this.getAttribute('data-label-remove') || '';
    this.emptyText = this.getAttribute('data-empty-text') || '';
    this.emptyCtaUrl = this.getAttribute('data-empty-cta-url') || '';
    this.emptyCtaText = this.getAttribute('data-empty-cta-text') || '';
    this.money = createMoney(this.locale, this.currency);

    const removeIconTpl = this.querySelector('[data-wishlist-remove-icon]');
    this.removeIcon = removeIconTpl && removeIconTpl.content ? removeIconTpl.content : null;

    subscribe(this);
    this.render();
  }

  disconnectedCallback() {
    unsubscribe(this);
  }

  render() {
    const items = readList();
    if (items.length === 0) {
      this.replaceChildren(this.buildEmpty());
      return;
    }
    const grid = document.createElement('ul');
    grid.className = 'wishlist-page__grid';
    grid.setAttribute('role', 'list');
    items.forEach((item) => grid.appendChild(this.buildCard(item)));
    this.replaceChildren(grid);
  }

  buildEmpty() {
    const wrap = document.createElement('div');
    wrap.className = 'wishlist-page__empty';

    const message = document.createElement('p');
    message.className = 'wishlist-page__empty-text';
    message.textContent = this.emptyText;
    wrap.appendChild(message);

    if (this.emptyCtaUrl && this.emptyCtaText) {
      const cta = document.createElement('a');
      cta.className = 'button button--secondary';
      cta.href = safeUrl(this.emptyCtaUrl);
      cta.textContent = this.emptyCtaText;
      wrap.appendChild(cta);
    }
    return wrap;
  }

  buildCard(item) {
    const li = document.createElement('li');
    li.className = 'featured-products__item wishlist-page__item';

    const article = document.createElement('article');
    article.className = 'product-card card';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'product-card__media';
    mediaLink.href = safeUrl(item.url);
    mediaLink.tabIndex = -1;
    mediaLink.setAttribute('aria-hidden', 'true');

    if (typeof item.image === 'string' && item.image) {
      const img = document.createElement('img');
      img.src = item.image;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 400;
      img.height = 400;
      mediaLink.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'product-card__info';

    const title = document.createElement('h3');
    title.className = 'product-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = safeUrl(item.url);
    titleLink.textContent = typeof item.title === 'string' ? item.title : '';
    title.appendChild(titleLink);

    const price = document.createElement('div');
    price.className = 'product-price product-price--small';
    const priceValue = document.createElement('span');
    priceValue.className = 'product-price__regular';
    priceValue.textContent = formatPrice(item.price, this.money);
    price.appendChild(priceValue);

    info.append(title, price);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'wishlist-page__remove';
    const removeLabel = this.labelRemove
      ? `${this.labelRemove} — ${typeof item.title === 'string' ? item.title : ''}`.trim()
      : '';
    remove.setAttribute('aria-label', removeLabel);
    remove.title = this.labelRemove;
    if (this.removeIcon) {
      remove.appendChild(this.removeIcon.cloneNode(true));
    } else {
      remove.textContent = '✕';
    }
    remove.addEventListener('click', () => removeItem(item.handle));

    article.append(mediaLink, info, remove);
    li.appendChild(article);
    return li;
  }
}

/* ------------------------------------------------------------------ */
/* Enregistrement                                                      */
/* ------------------------------------------------------------------ */

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
